'use client';

import { Suspense, useLayoutEffect, useRef } from 'react';

import { useParams, usePathname, useSearchParams } from 'next/navigation';

import { usePostHog } from 'posthog-js/react';

import {
  buildAccountUrlRegex,
  capturePageview,
  modalOwnsUrl,
  modalPageviewPending,
  pageKey,
  resetProAnalyticsClient,
  syncProAnalyticsClient
} from './PostHogAnalyticsProvider';
import { CookieConsentStatus } from '@hub/common/src/analytics-utils';

import { useCookieConsentStatus } from './CookieConsentStatusProvider';

export type PostHogSyncPropertiesProps = {
  accountId?: string;
  citOrigin: string;
  country?: string;
  region?: string;
  regionCode?: string;
  city?: string;
  latitude?: string;
  longitude?: string;
};

function PostHogPageViewCapture(syncArgs: PostHogSyncPropertiesProps) {
  const client = usePostHog();

  const pathname = usePathname();
  const searchParams = useSearchParams();
  // the [accountId] route param is the segment that identifies the account in the url: its slug, or its
  // id when it has no slug, which is what scopes autocapture to this account's pages. HUB Pro has no
  // [accountId] param, so it comes back undefined there and syncProAnalyticsClient falls back to the
  // accountId; harmless, since HUB Pro turns autocapture on for every page rather than using the allowlist
  const { accountId: accountUrlSegment } = useParams<{ accountId?: string }>();
  const hasCaptured = useRef(false);
  const lastCapturedPage = useRef<string | null>(null);
  const mountedAccountUrlSegment = useRef(accountUrlSegment);

  useLayoutEffect(() => {
    const search = searchParams.toString();
    const currentUrl = `${window.location.origin}${pathname}${search ? `?${search}` : ''}`;
    const page = pageKey(pathname, search);

    // a soft navigation can leave this instance's account route while the modal it lives in stays
    // mounted, since the router keeps a parallel slot's content. the params still name this account
    // then, only the pathname shows the url now belongs to another route and is not ours to claim
    const inOwnAccount =
      mountedAccountUrlSegment.current === undefined ||
      buildAccountUrlRegex(mountedAccountUrlSegment.current).test(pathname);

    // a modal carousel that swiped here owns this url and the group. a remount of the modal
    // (router.refresh) resets the group in the carousel's cleanup, so restore it, but never count the
    // page again: the one pageview left to send is the one a swipe could not while consent was pending
    if (modalOwnsUrl(currentUrl)) {
      if (inOwnAccount && !client.getGroups().account) {
        syncProAnalyticsClient(client, { ...syncArgs, accountSlug: accountUrlSegment });
      }
      if (!hasCaptured.current && modalPageviewPending(currentUrl)) {
        lastCapturedPage.current = page;
        capturePageview(client, currentUrl);
        hasCaptured.current = true;
      }
      return;
    }

    if (!inOwnAccount) {
      return;
    }

    // sync before capturing so the pageview carries this page's account; kept above the url guard so
    // a re-run after the reset below (dev strict mode) restores the group; it's a no-op when unchanged
    syncProAnalyticsClient(client, { ...syncArgs, accountSlug: accountUrlSegment });

    if (lastCapturedPage.current === page) {
      return;
    }

    lastCapturedPage.current = page;
    capturePageview(client, currentUrl);
    hasCaptured.current = true;
    // syncArgs is the props object, so it is a new reference on every render of the parent and this
    // effect re-runs then too, not only on navigation. Both halves are idempotent, which is what keeps
    // that harmless: the sync no-ops when nothing changed and the capture is deduped by url. Anything
    // added here must be idempotent as well
  }, [client, pathname, searchParams, syncArgs, accountUrlSegment]);

  // clear the account group and stop autocapture when this instance goes away, so events captured on routes
  // without an account (/, /search, /collections, ...) don't keep being credited to the last account visited
  useLayoutEffect(() => {
    return () => {
      // only clear the group if it is still the one this instance set
      if (client.getGroups().account === syncArgs.accountId) {
        resetProAnalyticsClient(client);
      }
    };
  }, [client, syncArgs.accountId]);

  return null;
}

export default function PostHogSyncProperties(props: PostHogSyncPropertiesProps) {
  const { hasConsent, consentLoaded } = useCookieConsentStatus();

  return props.accountId && consentLoaded ? (
    <Suspense fallback={null}>
      {/* key remounts the tracker, and so re-captures the pageview, once consent is granted; on reject
          posthog-js sends the pageview itself, so remounting there would double count it */}
      <PostHogPageViewCapture
        key={`${props.accountId}-${hasConsent === CookieConsentStatus.GRANTED}`}
        {...props}
      />
    </Suspense>
  ) : null;
}
